/**
 * Coordinate conversion utilities for Web Mercator projection
 * Used for converting between lat/lon and pixel coordinates in satellite imagery
 */

/**
 * Tile size constant for Web Mercator XYZ tiling system
 * Standard Web Mercator tiles are 256x256 pixels (Google Maps, ESRI, OpenStreetMap)
 * Note: This is for coordinate calculations, not the image fetch size
 */
export const TILE_SIZE = 256

/**
 * Georeferencing information for an ROI image
 */
export interface GeoReference {
  zoom: number
  mpp: number // Meters per pixel
  x0g: number // Global X pixel offset
  y0g: number // Global Y pixel offset
  H: number // Height in pixels
  W: number // Width in pixels
}

/**
 * Clamps latitude to the valid Web Mercator range (~±85.0511°)
 * to avoid projection infinities
 *
 * @param lat - Latitude in degrees
 * @returns Clamped latitude
 */
export function clampLat(lat: number): number {
  return Math.max(Math.min(lat, 85.05112878), -85.05112878)
}

/**
 * Converts (lat, lon) to global pixel coordinates in Web Mercator at zoom z
 * Global pixels refer to the full world map at that zoom level (tile-based coordinate system)
 * Used in ROI fetching to compute ROI bounds
 *
 * @param lat - Latitude in degrees
 * @param lon - Longitude in degrees
 * @param z - Zoom level
 * @returns [x, y] global pixel coordinates
 */
export function latLonToGlobalPx(lat: number, lon: number, z: number): [number, number] {
  lat = clampLat(lat)
  const siny = Math.sin((lat * Math.PI) / 180)
  const clampedSiny = Math.max(Math.min(siny, 0.9999), -0.9999)
  const scale = Math.pow(2, z) * TILE_SIZE

  const x = ((lon + 180.0) / 360.0) * scale
  const y = (0.5 - Math.log((1 + clampedSiny) / (1 - clampedSiny)) / (4 * Math.PI)) * scale

  return [x, y]
}

/**
 * Inverse transform: converts global pixel coordinates back to (lat, lon)
 *
 * @param x - Global X pixel coordinate
 * @param y - Global Y pixel coordinate
 * @param z - Zoom level
 * @returns [lat, lon] coordinates in degrees
 */
export function globalPxToLatLon(x: number, y: number, z: number): [number, number] {
  const scale = Math.pow(2, z) * TILE_SIZE
  const lon = (x / scale) * 360.0 - 180.0
  const n = Math.PI - (2.0 * Math.PI * y) / scale
  const lat = (Math.atan(Math.sinh(n)) * 180) / Math.PI

  return [lat, lon]
}

/**
 * Converts (lat, lon) into pixel coordinates within the ROI image
 * Used to get green center pixels
 *
 * @param lat - Latitude in degrees
 * @param lon - Longitude in degrees
 * @param georef - Georeferencing information
 * @returns [x, y] pixel coordinates in ROI image
 */
export function latLonToRoiPx(lat: number, lon: number, georef: GeoReference): [number, number] {
  const [gx, gy] = latLonToGlobalPx(lat, lon, georef.zoom)
  const x = gx - georef.x0g
  const y = gy - georef.y0g

  return [x, y]
}

/**
 * Inverse: converts ROI pixels back to coordinates
 *
 * @param x - ROI X pixel coordinate
 * @param y - ROI Y pixel coordinate
 * @param georef - Georeferencing information
 * @returns [lat, lon] coordinates in degrees
 */
export function roiPxToLatLon(x: number, y: number, georef: GeoReference): [number, number] {
  const gx = georef.x0g + x
  const gy = georef.y0g + y

  return globalPxToLatLon(gx, gy, georef.zoom)
}

/**
 * Computes meters per pixel at a given latitude and zoom level
 * Used in zoom selection and ROI sizing
 *
 * @param lat - Latitude in degrees
 * @param z - Zoom level
 * @returns Meters per pixel
 */
export function metersPerPixel(lat: number, z: number): number {
  const clampedLat = clampLat(lat)
  // Earth's circumenference / tile size
  const earthCircumference = 40075016.686 / TILE_SIZE
  // 156543.03392 = Earth's equatorial circumference (40,075,016.686 m) / 256 pixels
  // This is the meters per pixel at zoom 0 at the equator in Web Mercator
  // Formula: (Earth circumference / 256) * cos(latitude) / 2^zoom
  return (earthCircumference * Math.cos((clampedLat * Math.PI) / 180)) / Math.pow(2, z)
}

/**
 * Selects the optimal zoom for each hole depending on ROI size
 * and available zooms from ESRI
 *
 * Prefers 'prefer' zoom, but if it would make ROI absurdly huge in pixels,
 * steps down until radius fits within targetRadiusPx
 *
 * @param lat - Latitude in degrees
 * @param radiusM - Radius in meters
 * @param targetRadiusPx - Max allowed radius in pixels (default 240)
 * @param zMin - Minimum zoom level (default 20)
 * @param zMax - Maximum zoom level (default 22)
 * @param prefer - Preferred zoom when feasible (default 22)
 * @returns Selected zoom level
 */
export function chooseZoomForRadius(
  lat: number,
  radiusM: number,
  targetRadiusPx: number = 240,
  zMin: number = 20,
  zMax: number = 22,
  prefer: number = 22,
): number {
  prefer = Math.min(Math.max(prefer, zMin), zMax)

  // Start from preferred (e.g. 22) and step DOWN only if ROI would be too big
  for (let z = prefer; z >= zMin; z--) {
    const mpp = metersPerPixel(lat, z)
    const rpx = radiusM / mpp
    if (rpx <= targetRadiusPx) {
      return z
    }
  }

  return zMin
}
